import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import { Chart as ChartJS, RadialLinearScale, ArcElement, Tooltip, Legend, helpers } from "chart.js";
import { PolarArea } from "react-chartjs-2";
import { Box } from "@mui/material";
import { useTheme } from "@emotion/react";
import { useSelector } from "react-redux";
import { Capitalize, Pluralize } from "../../functions/Formatters";
import { global } from "../../context/global";

ChartJS.register(RadialLinearScale, ArcElement, Tooltip, Legend);

const createThemeTooltip = (themeLevels, themeDescriptions) => {
    return {
        enabled: false,
        external: function (context) {
            // Tooltip Element
            let tooltipEl = document.getElementById('chartjs-tooltip');

            // Create element on first render
            if (!tooltipEl) {
                tooltipEl = document.createElement('div');
                tooltipEl.id = 'chartjs-tooltip';
                tooltipEl.innerHTML = '<table style="font-size: 12px;"></table>';
                document.body.appendChild(tooltipEl);
            }

            // Hide if no tooltip
            const tooltipModel = context.tooltip;
            if (tooltipModel.opacity === 0) {
                tooltipEl.style.opacity = 0;
                return;
            }

            // Set caret Position
            tooltipEl.classList.remove('above', 'below', 'no-transform');
            if (tooltipModel.yAlign) {
                tooltipEl.classList.add(tooltipModel.yAlign);
            } else {
                tooltipEl.classList.add('no-transform');
            }

            // Set Text
            if (tooltipModel.body) {
                const titleLines = tooltipModel.title || [];
                const dataIndex = tooltipModel.dataPoints[0]?.dataIndex;
                const themeLevel = dataIndex !== undefined ? themeLevels[dataIndex] : '';
                const themeDescription = dataIndex !== undefined ? themeDescriptions[dataIndex] : '';

                let innerHtml = '<thead>';
                titleLines.forEach(function (title) {
                    innerHtml += '<tr><th>' + title + '</th></tr>';
                });
                innerHtml += '</thead><tbody>';

                // Add level information
                if (themeLevel !== undefined) {
                    const colors = tooltipModel.labelColors[0];
                    let style = 'background:' + colors.backgroundColor;
                    style += '; border-color:' + colors.borderColor;
                    style += '; border-width: 2px';
                    const span = '<span style="' + style + '"></span>';
                    innerHtml += '<tr><td>' + span + ' Niveau: ' + themeLevel + '</td></tr>';
                }

                // Add description
                if (themeDescription) {
                    innerHtml += '<tr><td>' + themeDescription + '</td></tr>';
                }

                innerHtml += '</tbody>';

                let tableRoot = tooltipEl.querySelector('table');
                tableRoot.innerHTML = innerHtml;
            }

            const position = context.chart.canvas.getBoundingClientRect();
            // const bodyFont = helpers.toFont(tooltipModel.options.bodyFont);

            // Display, position, and set styles for font
            tooltipEl.style.opacity = 1;
            tooltipEl.style.position = 'absolute';
            tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
            tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
            tooltipEl.style.fontSize = '12px';
            tooltipEl.style.padding = tooltipModel.padding + 'px ' + tooltipModel.padding + 'px';
            tooltipEl.style.pointerEvents = 'none';
            tooltipEl.style.background = 'rgba(0, 0, 0, 0.8)';
            tooltipEl.style.color = 'white';
            tooltipEl.style.borderRadius = '4px';
            tooltipEl.style.zIndex = '9999';
        }
    };
};

const PolarAreaChart = forwardRef(({ valueData, maxWidth = true }, ref) => {
    const availableThemes = useSelector((state) => state.themes.themes);
    const reorderedThemes = [...availableThemes.slice(3, availableThemes.length), ...availableThemes.slice(0, 3)];

    const style = useTheme();
    const superThemeColors = {
        0: style.palette.primary.light,
        1: style.palette.primary.dark,
        2: style.palette.secondary.main,
    };
    const themeColors = reorderedThemes.map((theme) => superThemeColors[theme.supertheme]);
    const themeLabels = reorderedThemes.map((theme) => theme.name);
    const themeDescriptions = reorderedThemes.map((theme) => theme.description);
    const themeLevels = reorderedThemes.map(
        (theme) => valueData.projectThemes.find((projectTheme) => projectTheme.theme == theme.id)?.importance || 0
    );

    const options = {
        title: {
            display: true,
            text: Capitalize(Pluralize(global.theme)),
        },
        responsive: true,
        responsiveAnimationDuration: 0,
        maintainAspectRatio: false,
        aspectRatio: 2,
        animation: false,
        scales: {
            r: {
                min: 0,
                max: 3,
                pointLabels: {
                    display: true,
                    centerPointLabels: true,
                    font: {
                        size: 14,
                    },
                },
                ticks: {
                    min: 0,
                    max: 3,
                    stepSize: 1,
                },
            },
        },
        plugins: {
            legend: {
                display: false,
                position: "top",
            },
            tooltip: createThemeTooltip(themeLevels, themeDescriptions),
        },
    };

    let data = {
        labels: themeLabels,
        datasets: [
            {
                label: "Niveau",
                data: themeLevels,
                backgroundColor: themeColors,
                borderWidth: 1,
            },
        ],
    };

    return (
        <PolarArea ref={ref} options={options} redraw={true} data={data} />
    );
});

PolarAreaChart.displayName = 'PolarAreaChart';

PolarAreaChart.propTypes = {
    valueData: PropTypes.shape({
        projectThemes: PropTypes.arrayOf(
            PropTypes.shape({
                theme: PropTypes.number,
                importance: PropTypes.number,
            })
        ).isRequired,
    }).isRequired,
};

export default PolarAreaChart;
